import { storableError } from '../util/errors';
import { usersAPI, listingsAPI } from '../api';
import { denormalisedResponseEntities } from '../util/data';

export const SEARCH_USERS_REQUEST = 'app/userSearch/SEARCH_USERS_REQUEST';
export const SEARCH_USERS_SUCCESS = 'app/userSearch/SEARCH_USERS_SUCCESS';
export const SEARCH_USERS_ERROR = 'app/userSearch/SEARCH_USERS_ERROR';
export const CLEAR_SEARCH_RESULTS = 'app/userSearch/CLEAR_SEARCH_RESULTS';

const initialState = {
  searchInProgress: false,
  searchError: null,
  searchResults: [],
  searchTerm: '',
  lastSearchTimestamp: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_USERS_REQUEST:
      return {
        ...state,
        searchInProgress: true,
        searchError: null,
        searchTerm: payload.searchTerm,
      };
    case SEARCH_USERS_SUCCESS:
      return {
        ...state,
        searchInProgress: false,
        searchResults: payload.users,
        lastSearchTimestamp: Date.now(),
      };
    case SEARCH_USERS_ERROR:
      return {
        ...state,
        searchInProgress: false,
        searchError: payload,
        searchResults: [],
      };
    case CLEAR_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: [],
        searchTerm: '',
      };
    default:
      return state;
  }
}

export const getSearchResults = state => state.userSearch.searchResults;
export const getSearchInProgress = state => state.userSearch.searchInProgress;
export const getSearchError = state => state.userSearch.searchError;

export const searchUsersRequest = searchTerm => ({
  type: SEARCH_USERS_REQUEST,
  payload: { searchTerm },
});

export const searchUsersSuccess = users => ({
  type: SEARCH_USERS_SUCCESS,
  payload: { users },
});

export const searchUsersError = error => ({
  type: SEARCH_USERS_ERROR,
  payload: error,
  error: true,
});

export const clearSearchResults = () => ({
  type: CLEAR_SEARCH_RESULTS,
});

let searchTimeout;
const DEBOUNCE_DELAY = 300;

export const searchUsers = (searchTerm = '', options = {}) => (dispatch, getState, sdk) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!searchTerm || searchTerm.length === 0) {
    dispatch(clearSearchResults());
    return Promise.resolve([]);
  }

  if (searchTerm.length < 1) {
    return Promise.resolve([]);
  }

  return new Promise((resolve, reject) => {
    searchTimeout = setTimeout(() => {
      dispatch(searchUsersRequest(searchTerm));

      const { limit = 10 } = options;
      const queryParams = {
        searchTerm,
        limit,
      };

      usersAPI
        .search(queryParams)
        .then(response => {
          const users = denormalisedResponseEntities(response.data);
          dispatch(searchUsersSuccess(users));
          resolve(users);
        })
        .catch(e => {
          const error = storableError(e);
          dispatch(searchUsersError(error));
          reject(error);
        });
    }, DEBOUNCE_DELAY);
  });
};

export const searchUsersSync = (users, searchTerm, maxResults = 5) => {
  if (!users || users.length === 0) {
    return [];
  }

  if (!searchTerm || searchTerm.length === 0) {
    return users.slice(0, maxResults);
  }

  const searchLower = searchTerm.toLowerCase();

  const scoredUsers = users
    .map(user => {
      const profile = user.attributes.profile;
      const publicData = profile.publicData || {};

      const displayName = publicData.name?.displayName || '';
      const firstName = profile.firstName || '';
      const lastName = profile.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();

      let score = 0;

      if (displayName.toLowerCase().startsWith(searchLower)) {
        score = 100;
      } else if (firstName.toLowerCase().startsWith(searchLower)) {
        score = 90;
      } else if (lastName.toLowerCase().startsWith(searchLower)) {
        score = 80;
      } else if (displayName.toLowerCase().includes(searchLower)) {
        score = 70;
      } else if (fullName.toLowerCase().includes(searchLower)) {
        score = 60;
      } else if (firstName.toLowerCase().includes(searchLower)) {
        score = 50;
      } else if (lastName.toLowerCase().includes(searchLower)) {
        score = 40;
      }

      return { user, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(({ user }) => user);

  return scoredUsers;
};
